import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ActivityEvent, ActivityKind } from '@/types'

import { cn } from '@/lib/utils'

import {
    Repeat,
    RotateCcw,
    TrendingDown,
    TrendingUp,
    TriangleAlert,
    UserMinus,
    UserPlus
} from 'lucide-react'

import {
    formatCurrency,
    formatDateTime,
    formatRelative,
    formatSignedCurrency,
    formatTime
} from '@/lib/format'

interface KindMeta {
    label: string
    icon: LucideIcon
    tone: string
}

export const ACTIVITY_KIND_META: Record<ActivityKind, KindMeta> = {
    signup: { label: 'New customer', icon: UserPlus, tone: 'bg-primary/10 text-primary' },
    upgrade: { label: 'Upgrade', icon: TrendingUp, tone: 'bg-success-soft text-success' },
    downgrade: { label: 'Downgrade', icon: TrendingDown, tone: 'bg-warning-soft text-warning' },
    plan_change: { label: 'Plan change', icon: Repeat, tone: 'bg-secondary text-muted-foreground' },
    churn: { label: 'Cancellation', icon: UserMinus, tone: 'bg-danger-soft text-danger' },
    payment_failed: { label: 'Failed payment', icon: TriangleAlert, tone: 'bg-danger-soft text-danger' },
    refund: { label: 'Refund', icon: RotateCcw, tone: 'bg-secondary text-muted-foreground' }
}

const CustomerName: FC<{ event: ActivityEvent }> = ({ event }): ReactNode => {
    return (
        <span data-mdlfy-cms={`customers:${event.customerId}:name`} className='font-semibold text-foreground'>
            {event.customerName}
        </span>
    )
}

const PlanName: FC<{ id: string | null, name: string | null }> = ({ id, name }): ReactNode => {
    if (!id || !name) return null

    return (
        <span data-mdlfy-cms={`plans:${id}:name`} className='font-medium text-foreground'>
            {name}
        </span>
    )
}

const describe = (event: ActivityEvent): ReactNode => {
    const name = <CustomerName event={event} />
    const plan = <PlanName id={event.planId} name={event.planName} />
    const previous = <PlanName id={event.previousPlanId} name={event.previousPlanName} />
    const hasPlans = Boolean(event.planName && event.previousPlanName)

    switch (event.kind) {
        case 'signup':
            return event.planName ? <>{name} signed up for the {plan} plan</> : <>{name} signed up</>
        case 'upgrade':
            return hasPlans ? <>{name} upgraded from {previous} to {plan}</> : <>{name} upgraded their plan</>
        case 'downgrade':
            return hasPlans ? <>{name} moved down from {previous} to {plan}</> : <>{name} moved to a smaller plan</>
        case 'plan_change':
            return hasPlans ? <>{name} switched from {previous} to {plan}</> : <>{name} switched plans</>
        case 'churn':
            return event.planName ? <>{name} cancelled their {plan} plan</> : <>{name} cancelled their subscription</>
        case 'payment_failed':
            return <>A payment from {name} failed</>
        case 'refund':
            return <>{name} was refunded</>
    }
}

const impactFor = (event: ActivityEvent): string | null => {
    switch (event.kind) {
        case 'signup':
            return event.planPrice !== null ? `${formatSignedCurrency(event.planPrice)}/mo` : null
        case 'upgrade':
        case 'downgrade':
        case 'plan_change':
            return event.planPrice !== null && event.previousPlanPrice !== null
                ? `${formatSignedCurrency(event.planPrice - event.previousPlanPrice)}/mo`
                : null
        case 'churn':
            return event.planPrice !== null ? `${formatSignedCurrency(-event.planPrice)}/mo` : null
        case 'payment_failed':
        case 'refund':
            return event.amount !== null ? formatCurrency(event.amount) : null
    }
}

interface ActivityItemProps {
    event: ActivityEvent
    now: string
    timeStyle?: 'relative' | 'clock'
}

export const ActivityItem: FC<ActivityItemProps> = ({ event, now, timeStyle = 'relative' }): ReactNode => {
    const meta = ACTIVITY_KIND_META[event.kind]
    const Icon = meta.icon
    const impact = impactFor(event)

    return (
        <li className='flex items-start gap-3 py-3.5'>
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', meta.tone)}>
                <Icon aria-hidden='true' className='size-5' />
            </span>

            <div className='min-w-0 flex-1'>
                <p className='text-sm leading-snug text-muted-foreground'>
                    {describe(event)}
                </p>

                <p className='mt-1 text-xs text-muted-foreground'>
                    {meta.label}
                    <span aria-hidden='true'> · </span>
                    <time dateTime={event.occurredAt} title={`${formatDateTime(event.occurredAt)} UTC`}>
                        {timeStyle === 'clock' ? formatTime(event.occurredAt) : formatRelative(event.occurredAt, now)}
                    </time>
                </p>
            </div>

            {impact && (
                <span className='shrink-0 pt-0.5 text-sm font-semibold whitespace-nowrap text-foreground tabular-nums'>
                    {impact}
                </span>
            )}
        </li>
    )
}
